"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useRef, useState } from "react";
import { ChevronRight, PanelLeft, PanelLeftClose } from "lucide-react";
import { sidebarItems } from "./data";
import { SideBarItem, SidebarItemsLink } from "./types";
import { useParams, usePathname, useRouter } from "next/navigation";
import { ColorModeContext } from "@/contexts/color-mode";
import { useSideBarCollapse } from "@/store/sideBarCollapse";
import { cn } from "@utils/cn";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@components/ui/DropdownMenu";
import { Separator } from "@components/ui/separator";
export const Sidebar = () => {
  const $ = _c(81);
  const pathname = usePathname();
  const params = useParams();
  const {
    mode,
    primaryColor
  } = useContext(ColorModeContext);
  const [openItemTitle, setOpenItemTitle] = useState(null);
  const [hoveredItemTitle, setHoveredItemTitle] = useState(null);
  const closeMenuTimeoutRef = useRef(null);
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname.split("/");
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const url = t0[2];
  const branchName = Array.isArray(params.branch_id) ? params.branch_id[0] : params.branch_id;
  const collapsed = useSideBarCollapse(_temp);
  const toggle = useSideBarCollapse(_temp2);
  const router = useRouter();
  let t1;
  if ($[2] !== branchName) {
    t1 = link => {
      if (!link || link === "/") {
        return `/${branchName ?? ""}`;
      }
      if (link.startsWith("http")) {
        return link;
      }
      if (!branchName) {
        return link.startsWith("/") ? link : `/${link}`;
      }
      return `/${branchName ?? ""}/${link.replace(/^\/+/, "")}`;
    };
    $[2] = branchName;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getBranchPath = t1;
  let t2;
  if ($[4] !== getBranchPath || $[5] !== router) {
    t2 = (link_0, newTab) => {
      if (!link_0) {
        return;
      }
      const href = getBranchPath(link_0);
      if (newTab || link_0.startsWith("http")) {
        window.open(href, "_blank");
        return;
      }
      router.push(href);
    };
    $[4] = getBranchPath;
    $[5] = router;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleNavigation = t2;
  let t3;
  if ($[7] !== getBranchPath || $[8] !== pathname || $[9] !== url) {
    t3 = item => {
      const hasDirectLink = Boolean(item.link);
      return item.url && url === item.url || hasDirectLink && (pathname === getBranchPath(item.link) || pathname === item.link);
    };
    $[7] = getBranchPath;
    $[8] = pathname;
    $[9] = url;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const isItemActive = t3;
  let t4;
  if ($[11] !== getBranchPath || $[12] !== pathname) {
    t4 = link_1 => {
      if (!link_1) {
        return false;
      }
      const href_0 = getBranchPath(link_1).replace(/\/+$/, "");
      const currentPath = pathname.replace(/\/+$/, "");
      return currentPath === href_0 || currentPath.includes(link_1);
    };
    $[11] = getBranchPath;
    $[12] = pathname;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const isLinkActive = t4;
  let t5;
  if ($[14] !== hoveredItemTitle || $[15] !== isItemActive || $[16] !== mode || $[17] !== openItemTitle || $[18] !== primaryColor) {
    t5 = item_0 => {
      const isHovered = hoveredItemTitle === item_0.title || openItemTitle === item_0.title;
      const isActive = isItemActive(item_0);
      if (isActive && isHovered) {
        return mode === "dark" ? `color-mix(in srgb, ${primaryColor} 74%, white)` : `color-mix(in srgb, ${primaryColor} 82%, white)`;
      }
      if (isActive) {
        return mode === "dark" ? `color-mix(in srgb, ${primaryColor} 72%, black)` : `color-mix(in srgb, ${primaryColor} 78%, black)`;
      }
      if (isHovered) {
        return mode === "dark" ? `color-mix(in srgb, ${primaryColor} 62%, white)` : `color-mix(in srgb, ${primaryColor} 86%, white)`;
      }
      return "transparent";
    };
    $[14] = hoveredItemTitle;
    $[15] = isItemActive;
    $[16] = mode;
    $[17] = openItemTitle;
    $[18] = primaryColor;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const getItemBackground = t5;
  let t6;
  if ($[20] !== mode || $[21] !== primaryColor) {
    t6 = isSelected => ({
      "--sidebar-subitem-bg": isSelected ? mode === "dark" ? `color-mix(in srgb, ${primaryColor} 28%, transparent)` : `color-mix(in srgb, ${primaryColor} 18%, transparent)` : "transparent",
      "--sidebar-subitem-hover-bg": isSelected ? mode === "dark" ? `color-mix(in srgb, ${primaryColor} 58%, transparent)` : `color-mix(in srgb, ${primaryColor} 42%, transparent)` : mode === "dark" ? `color-mix(in srgb, ${primaryColor} 44%, transparent)` : `color-mix(in srgb, ${primaryColor} 28%, transparent)`
    }) as React.CSSProperties;
    $[20] = mode;
    $[21] = primaryColor;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const getSubItemStyle = t6;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t7;
  let t8;
  let t9;
  if ($[23] !== collapsed || $[24] !== getItemBackground || $[25] !== getSubItemStyle || $[26] !== handleNavigation || $[27] !== isLinkActive || $[28] !== mode || $[29] !== openItemTitle || $[30] !== primaryColor || $[31] !== toggle) {
    const cancelCloseMenu = () => {
      if (closeMenuTimeoutRef.current) {
        clearTimeout(closeMenuTimeoutRef.current);
        closeMenuTimeoutRef.current = null;
      }
    };
    const openMenu = item_1 => {
      if (!item_1.sidebarItemsLinks?.length) {
        return;
      }
      cancelCloseMenu();
      setHoveredItemTitle(item_1.title);
      setOpenItemTitle(item_1.title);
    };
    const scheduleCloseMenu = () => {
      cancelCloseMenu();
      closeMenuTimeoutRef.current = setTimeout(() => {
        setHoveredItemTitle(null);
        setOpenItemTitle(null);
      }, 120);
    };
    let t16;
    if ($[41] !== getSubItemStyle || $[42] !== handleNavigation || $[43] !== isLinkActive || $[44] !== mode) {
      t16 = subLink => {
        const isSelected_0 = isLinkActive(subLink.link);
        return <DropdownMenuItem key={subLink.link} onSelect={() => handleNavigation(subLink.link, subLink.newTab)} style={getSubItemStyle(isSelected_0)} className={cn("cursor-pointer gap-3 !bg-[var(--sidebar-subitem-bg)] hover:!bg-[var(--sidebar-subitem-hover-bg)] focus:!bg-[var(--sidebar-subitem-hover-bg)] data-[highlighted]:!bg-[var(--sidebar-subitem-hover-bg)]", mode === "dark" ? "white-icon" : "black-icon", isSelected_0 && "font-medium text-primary")}><span className="shrink-0">{subLink.icon}</span><span className="truncate">{subLink.title}</span></DropdownMenuItem>;
      };
      $[41] = getSubItemStyle;
      $[42] = handleNavigation;
      $[43] = isLinkActive;
      $[44] = mode;
      $[45] = t16;
    } else {
      t16 = $[45];
    }
    const renderSubLink = t16;
    let t17;
    if ($[46] !== getSubItemStyle || $[47] !== handleNavigation || $[48] !== isLinkActive || $[49] !== mode || $[50] !== renderSubLink) {
      t17 = subItem => {
        const isActive_0 = isLinkActive(subItem.link) || Boolean(subItem.subLinks?.some(subLink_0 => isLinkActive(subLink_0.link)));
        if (subItem.subLinks?.length) {
          return <DropdownMenuSub key={subItem.title}><DropdownMenuSubTrigger style={getSubItemStyle(isActive_0)} className={cn("cursor-pointer gap-3 !bg-[var(--sidebar-subitem-bg)] hover:!bg-[var(--sidebar-subitem-hover-bg)] focus:!bg-[var(--sidebar-subitem-hover-bg)] data-[highlighted]:!bg-[var(--sidebar-subitem-hover-bg)]", mode === "dark" ? "white-icon" : "black-icon", isActive_0 && "font-medium text-primary")}><span className="shrink-0">{subItem.icon}</span><span className="truncate">{subItem.title}</span></DropdownMenuSubTrigger><DropdownMenuSubContent>{subItem.subLinks.map(renderSubLink)}</DropdownMenuSubContent></DropdownMenuSub>;
        }
        return <DropdownMenuItem key={subItem.title} onSelect={() => handleNavigation(subItem.link, subItem.newTab)} style={getSubItemStyle(isActive_0)} className={cn("cursor-pointer gap-3 !bg-[var(--sidebar-subitem-bg)] hover:!bg-[var(--sidebar-subitem-hover-bg)] focus:!bg-[var(--sidebar-subitem-hover-bg)] data-[highlighted]:!bg-[var(--sidebar-subitem-hover-bg)]", mode === "dark" ? "white-icon" : "black-icon", isActive_0 && "font-medium text-primary")}><span className="shrink-0">{subItem.icon}</span><span className="truncate">{subItem.title}</span></DropdownMenuItem>;
      };
      $[46] = getSubItemStyle;
      $[47] = handleNavigation;
      $[48] = isLinkActive;
      $[49] = mode;
      $[50] = renderSubLink;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    const renderDropdownItem = t17;
    const renderSidebarItem = (item_2, index) => {
      if (item_2.title === "divider") {
        return <Separator key={`${item_2.title}-${index}`} className="my-2 bg-white/20" />;
      }
      const itemButton = <button type="button" title={collapsed ? item_2.title : undefined} className={cn("white-icon flex h-11 w-full items-center rounded-lg px-3 text-left text-sm font-medium text-white outline-none transition-colors focus-visible:ring-2 focus-visible:ring-white/60", collapsed ? "justify-center" : "justify-start gap-3")} style={{
        backgroundColor: getItemBackground(item_2)
      }} onMouseEnter={() => {
        setHoveredItemTitle(item_2.title);
        openMenu(item_2);
      }} onMouseLeave={scheduleCloseMenu} onClick={() => {
        if (!item_2.sidebarItemsLinks?.length) {
          handleNavigation(item_2.link, item_2.newTab);
        }
      }}><span className="shrink-0">{item_2.icon}</span>{!collapsed && <><span className="min-w-0 flex-1 truncate">{item_2.title}</span>{item_2.sidebarItemsLinks?.length ? <ChevronRight className="size-4 shrink-0 opacity-80" /> : null}</>}</button>;
      if (!item_2.sidebarItemsLinks?.length) {
        return <div key={item_2.title}>{itemButton}</div>;
      }
      return <DropdownMenu key={item_2.title} modal={false} open={openItemTitle === item_2.title} onOpenChange={open => setOpenItemTitle(open ? item_2.title : null)}><DropdownMenuTrigger asChild={true}>{itemButton}</DropdownMenuTrigger><DropdownMenuContent side="right" align="start" sideOffset={8} className="min-w-64" onMouseEnter={() => {
          cancelCloseMenu();
          setHoveredItemTitle(item_2.title);
        }} onMouseLeave={scheduleCloseMenu}><div className="flex items-center gap-3 px-2 py-2 text-sm font-semibold text-primary"><span className={mode === "dark" ? "white-icon" : "black-icon"}>{item_2.icon}</span><span>{item_2.title}</span></div><Separator className="my-1" />{item_2.sidebarItemsLinks.map(renderDropdownItem)}</DropdownMenuContent></DropdownMenu>;
    };
    t14 = "flex h-full max-h-full min-h-0 shrink-0 flex-col overflow-hidden p-2 pr-0 transition-all duration-300";
    const t18 = collapsed ? 80 : 260;
    if ($[52] !== t18) {
      t15 = {
        width: t18
      };
      $[52] = t18;
      $[53] = t15;
    } else {
      t15 = $[53];
    }
    t9 = "scrollbar-hide min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-2xl py-4 shadow-[0_12px_30px_hsl(var(--overlay)/0.18)] ring-1 ring-white/10";
    const t19 = `linear-gradient(180deg, ${primaryColor} 0%, color-mix(in srgb, ${primaryColor} 78%, black) 100%)`;
    if ($[54] !== t19) {
      t10 = {
        width: "100%",
        background: t19
      };
      $[54] = t19;
      $[55] = t10;
    } else {
      t10 = $[55];
    }
    t11 = "Main navigation";
    const t20 = collapsed ? "justify-center" : "justify-start gap-3";
    let t21;
    if ($[56] !== t20) {
      t21 = cn("white-icon flex h-10 w-full items-center rounded-xl bg-white/10 px-3 text-sm font-medium text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)] transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60", t20);
      $[56] = t20;
      $[57] = t21;
    } else {
      t21 = $[57];
    }
    const t22 = collapsed ? "Expand sidebar" : "Collapse sidebar";
    const t23 = collapsed ? "Expand sidebar" : undefined;
    let t24;
    if ($[58] !== collapsed) {
      t24 = collapsed ? <PanelLeft className="size-4 shrink-0" /> : <PanelLeftClose className="size-4 shrink-0" />;
      $[58] = collapsed;
      $[59] = t24;
    } else {
      t24 = $[59];
    }
    if ($[60] !== t21 || $[61] !== t22 || $[62] !== t23 || $[63] !== t24 || $[64] !== toggle) {
      t12 = <div className="px-2 pb-3"><button type="button" onClick={toggle} className={t21} aria-label={t22} title={t23}>{t24}</button></div>;
      $[60] = t21;
      $[61] = t22;
      $[62] = t23;
      $[63] = t24;
      $[64] = toggle;
      $[65] = t12;
    } else {
      t12 = $[65];
    }
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <Separator className="mx-2 mb-3 bg-white/20" />;
      $[66] = t13;
    } else {
      t13 = $[66];
    }
    t7 = "flex flex-col gap-1 px-2";
    t8 = sidebarItems.map(renderSidebarItem);
    $[23] = collapsed;
    $[24] = getItemBackground;
    $[25] = getSubItemStyle;
    $[26] = handleNavigation;
    $[27] = isLinkActive;
    $[28] = mode;
    $[29] = openItemTitle;
    $[30] = primaryColor;
    $[31] = toggle;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
  } else {
    t10 = $[32];
    t11 = $[33];
    t12 = $[34];
    t13 = $[35];
    t14 = $[36];
    t15 = $[37];
    t7 = $[38];
    t8 = $[39];
    t9 = $[40];
  }
  let t16;
  if ($[67] !== t7 || $[68] !== t8) {
    t16 = <div className={t7}>{t8}</div>;
    $[67] = t7;
    $[68] = t8;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  let t17;
  if ($[70] !== t10 || $[71] !== t11 || $[72] !== t12 || $[73] !== t13 || $[74] !== t16 || $[75] !== t9) {
    t17 = <nav className={t9} style={t10} aria-label={t11}>{t12}{t13}{t16}</nav>;
    $[70] = t10;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
    $[74] = t16;
    $[75] = t9;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== t14 || $[78] !== t15 || $[79] !== t17) {
    t18 = <aside className={t14} style={t15}>{t17}</aside>;
    $[77] = t14;
    $[78] = t15;
    $[79] = t17;
    $[80] = t18;
  } else {
    t18 = $[80];
  }
  return t18;
};
function _temp(state) {
  return state.collapsed;
}
function _temp2(state_0) {
  return state_0.toggle;
}